import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { CalendarClock, Bell, ArrowRight, FileText } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import usePageTitle from "@/lib/usePageTitle";
import { parseLocalDate, formatShortDate } from "@/lib/dates";

const DEFAULT_HERO_IMG = "https://images.unsplash.com/photo-1590256446678-51a8e0a3c70b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const formatDate = (iso) => formatShortDate(iso);

const SectionUpcoming = ({ events }) => (
  <Card className="md:col-span-5 border-border" data-testid="upcoming-meetings-card">
    <CardContent className="p-7">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-secondary">Township Board</div>
          <h2 className="font-heading text-2xl font-semibold mt-1">Upcoming Meetings</h2>
        </div>
        <CalendarClock className="text-accent" />
      </div>
      <ul className="space-y-4">
        {events.length === 0 && <li className="text-sm text-muted-foreground">No upcoming meetings posted yet.</li>}
        {events.map((e) => (
          <li key={e.id} className="flex gap-4 pb-4 border-b border-border last:border-b-0 last:pb-0" data-testid={`event-${e.id}`}>
            <div className="shrink-0 w-16 text-center">
              <div className="text-2xl font-heading font-semibold text-primary leading-none">
                {parseLocalDate(e.event_date).getDate()}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
                {parseLocalDate(e.event_date).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
              </div>
            </div>
            <div>
              <div className="font-medium">{e.title}</div>
              <div className="text-sm text-muted-foreground">{e.time} · {e.location}</div>
            </div>
          </li>
        ))}
      </ul>
      <Link to="/calendar" className="inline-flex items-center gap-1 text-sm text-secondary mt-5 link-underline" data-testid="see-calendar-link">
        See full calendar <ArrowRight size={14} />
      </Link>
    </CardContent>
  </Card>
);

const SectionNotices = ({ notices }) => (
  <Card className="md:col-span-7 border-border bg-primary text-primary-foreground" data-testid="notices-card">
    <CardContent className="p-7">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent">Important</div>
          <h2 className="font-heading text-2xl font-semibold mt-1">Notices & Alerts</h2>
        </div>
        <Bell className="text-accent" />
      </div>
      <ul className="space-y-5">
        {notices.length === 0 && <li className="text-sm text-primary-foreground/60">No pinned notices.</li>}
        {notices.map((n) => (
          <li key={n.id} className="border-l-2 border-accent pl-4" data-testid={`notice-${n.id}`}>
            <div className="font-heading font-semibold">{n.title}</div>
            <p className="text-sm text-primary-foreground/80 mt-1 line-clamp-2 leading-relaxed">{n.body}</p>
          </li>
        ))}
      </ul>
      <Link to="/information" className="inline-flex items-center gap-1 text-sm text-accent mt-5 link-underline" data-testid="see-information-link">
        Read all notices <ArrowRight size={14} />
      </Link>
    </CardContent>
  </Card>
);

const SectionMinutes = ({ minutes }) => (
  <Card className="md:col-span-8 border-border" data-testid="minutes-card">
    <CardContent className="p-7">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-secondary">Recent</div>
          <h2 className="font-heading text-2xl font-semibold mt-1">Meeting Minutes</h2>
        </div>
        <FileText className="text-accent" />
      </div>
      <ul className="divide-y divide-border">
        {minutes.map((m) => (
          <li key={m.id} className="py-3" data-testid={`minutes-row-${m.id}`}>
            <Link to={`/minutes/${m.id}`} className="flex justify-between items-center group">
              <div>
                <div className="font-medium group-hover:text-primary transition-colors">{m.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{m.summary}</div>
              </div>
              <div className="text-xs text-muted-foreground shrink-0 ml-4">{formatDate(m.meeting_date)}</div>
            </Link>
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);

const SectionQuick = () => (
  <Card className="md:col-span-4 border-border bg-muted/40" data-testid="quick-links-card">
    <CardContent className="p-7">
      <div className="text-xs uppercase tracking-[0.2em] text-secondary">Around the township</div>
      <h2 className="font-heading text-2xl font-semibold mt-1 mb-5">Quick Links</h2>
      <ul className="space-y-3 text-sm">
        <li><Link to="/postings" className="link-underline">Public Postings & notices</Link></li>
        <li><Link to="/board" className="link-underline">Township Board members</Link></li>
        <li><Link to="/projects" className="link-underline">Projects & initiatives</Link></li>
        <li><Link to="/information" className="link-underline">Wild parsnip & safety notices</Link></li>
        <li><Link to="/links" className="link-underline">State & county resources</Link></li>
        <li><Link to="/contact" className="link-underline">Send a feedback message</Link></li>
      </ul>
    </CardContent>
  </Card>
);

const SECTION_MAP = {
  upcoming: SectionUpcoming,
  notices: SectionNotices,
  minutes: SectionMinutes,
  quick: SectionQuick,
};

const Home = () => {
  usePageTitle("Home");
  const [data, setData] = useState({ events: [], minutes: [], notices: [] });
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/events"),
      api.get("/minutes"),
      api.get("/notices"),
      api.get("/settings"),
    ])
      .then(([e, m, n, s]) => {
        setData({
          events: e.data.slice(0, 3),
          minutes: m.data.slice(0, 3),
          notices: n.data.filter((x) => x.pinned).slice(0, 3),
        });
        setSettings(s.data);
      })
      .catch(() => {});
  }, []);

  const heroImg = settings?.hero_file_id ? `${API_BASE}/files/${settings.hero_file_id}` : DEFAULT_HERO_IMG;
  const heroTitle = settings?.hero_title || "A lakeside township, rooted in community.";
  const heroSubtitle = settings?.hero_subtitle || "";

  const sections = settings?.home_sections || [
    { key: "upcoming", visible: true },
    { key: "notices", visible: true },
    { key: "minutes", visible: true },
    { key: "quick", visible: true },
  ];

  return (
    <div data-testid="home-page">
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroImg})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/70 via-primary/40 to-primary/80" />
        <div className="relative container-le pt-24 pb-32 md:pt-32 md:pb-40 text-white">
          <div className="max-w-3xl fade-up">
            <div className="text-xs uppercase tracking-[0.25em] text-accent mb-5">Becker County · Minnesota</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium leading-[1.05] tracking-tight" data-testid="hero-title">
              {heroTitle}
            </h1>
            {heroSubtitle && (
              <p className="text-lg text-white/85 mt-6 max-w-xl leading-relaxed" data-testid="hero-subtitle">
                {heroSubtitle}
              </p>
            )}
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/minutes">
                <Button className="bg-accent hover:bg-accent/90 text-accent-foreground px-6 py-2.5" data-testid="hero-cta-minutes">
                  Meeting Minutes <ArrowRight size={16} className="ml-1.5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white/40 text-white hover:bg-white hover:text-primary px-6 py-2.5" data-testid="hero-cta-contact">
                  Contact the Board
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="container-le section-pad">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="home-sections-grid">
          {sections.filter((s) => s.visible).map((s) => {
            const Comp = SECTION_MAP[s.key];
            if (!Comp) return null;
            return (
              <Comp
                key={s.key}
                events={data.events}
                minutes={data.minutes}
                notices={data.notices}
              />
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Home;
